import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import API from "../../../services";
import {
  WhitelabelPackagePicker,
  parseWhitelabelPackagePaymentMethods,
  type WhitelabelPackageCard,
} from "./WhitelabelPackagePicker";

const BTN_PRIMARY =
  "font-semibold shadow-[0_2px_12px_color-mix(in_srgb,var(--border-color)_25%,transparent)] transition-[opacity,transform] hover:opacity-95 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:opacity-45 disabled:cursor-not-allowed";
const BTN_PRIMARY_CLASS = `mt-6 w-full p-3 text-sm text-white rounded-lg border-2 border-[color-mix(in_srgb,var(--border-color)_50%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_28%,#0c1222)] ${BTN_PRIMARY}`;
const BTN_SECONDARY_CLASS =
  "mt-3 w-full p-2.5 text-sm text-zinc-300 rounded-lg border border-zinc-600/90 bg-zinc-800/60 transition-colors hover:bg-zinc-700/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent";

type LocationState = { returnTo?: string; whitelabelId?: string } | null | undefined;

const mapApiRowToPackageCard = (raw: Record<string, unknown>): WhitelabelPackageCard | null => {
  const packageId = String(raw.packageId ?? raw.package_id ?? "").trim();
  if (!packageId) return null;
  const pointsCredited = Number(raw.pointsCredited ?? raw.points_credited ?? 0);
  const payAmount = Number(raw.payAmount ?? raw.pay_amount ?? 0);
  const discountAmountRaw = raw.discountAmount ?? raw.discount_amount;
  const discountAmount =
    discountAmountRaw != null && discountAmountRaw !== ""
      ? (() => {
          const n = Number(discountAmountRaw);
          return Number.isFinite(n) ? n : undefined;
        })()
      : undefined;
  const originalRaw = raw.originalPayAmount ?? raw.original_pay_amount;
  const originalPayAmount =
    originalRaw != null && originalRaw !== ""
      ? (() => {
          const n = Number(originalRaw);
          return Number.isFinite(n) ? n : undefined;
        })()
      : undefined;
  const discountRaw = raw.packageDiscountPercent ?? raw.package_discount_percent;
  const packageDiscountPercent =
    discountRaw != null && discountRaw !== ""
      ? (() => {
          const n = Number(discountRaw);
          return Number.isFinite(n) ? n : undefined;
        })()
      : undefined;
  return {
    packageId,
    name: raw.name != null ? String(raw.name) : undefined,
    pointsCredited: Number.isFinite(pointsCredited) ? pointsCredited : 0,
    payAmount: Number.isFinite(payAmount) ? payAmount : 0,
    discountAmount,
    originalPayAmount,
    currency: raw.currency != null ? String(raw.currency) : raw.currencyCode != null ? String(raw.currencyCode) : "INR",
    packageDiscountPercent,
    paymentMethods: parseWhitelabelPackagePaymentMethods(raw),
  };
};

const WhitelabelAddFundsPackages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as LocationState;
  const returnTo = routeState?.returnTo;
  const whitelabelIdFromState = String(routeState?.whitelabelId ?? "").trim();

  const [packages, setPackages] = useState<WhitelabelPackageCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);

  useEffect(() => {
    if (!whitelabelIdFromState) {
      setPackages([]);
      setLoading(false);
      setError(null);
      setSelectedPackageId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await API.post("whitelabel/packages", { whitelabelId: whitelabelIdFromState });
        if (cancelled) return;
        if (response?.data?.status !== "RS_OK") {
          const msg = String(response?.data?.errorDescription || "Could not load packages.").trim();
          setPackages([]);
          setError(msg || "Could not load packages.");
          return;
        }
        const rawList = response?.data?.packages;
        const list: WhitelabelPackageCard[] = Array.isArray(rawList)
          ? rawList
              .map((row: unknown) =>
                row && typeof row === "object"
                  ? mapApiRowToPackageCard(row as Record<string, unknown>)
                  : null
              )
              .filter((x): x is WhitelabelPackageCard => x != null)
          : [];
        setPackages(list);
        setSelectedPackageId((prev) => {
          const prevStr = String(prev ?? "").trim();
          if (prevStr && list.some((x) => x.packageId === prevStr)) return prevStr;
          return list[0]?.packageId ?? null;
        });
      } catch {
        if (!cancelled) {
          setPackages([]);
          setError("Could not load packages.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [whitelabelIdFromState, retryNonce]);

  const formatPackagePoints = (n: number) =>
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);

  const formatPackagePrice = (amount: number, currency = "INR") => {
    try {
      const locale = currency === "INR" ? "en-IN" : undefined;
      const safeAmount = Number.isFinite(amount) ? amount : 0;
      const roundedAmount = Math.round(safeAmount);
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(roundedAmount);
    } catch {
      const safeAmount = Number.isFinite(amount) ? amount : 0;
      return `${Math.round(safeAmount)} ${currency}`;
    }
  };

  const goBack = () => {
    if (returnTo && typeof returnTo === "string" && returnTo.startsWith("/")) {
      navigate(returnTo);
      return;
    }
    navigate("/whitelabel");
  };

  const onContinue = useCallback(() => {
    if (!selectedPackageId) return;
    if (!whitelabelIdFromState) {
      toast.error("Missing brand context. Go back and open Add funds from your brand page.");
      return;
    }
    const pkg = packages.find((p) => p.packageId === selectedPackageId);
    if (!pkg) return;

    const qs = new URLSearchParams();
    qs.set("whitelabelId", whitelabelIdFromState);
    qs.set("packageId", pkg.packageId);
    qs.set("resumePayment", "1");

    navigate(`/whitelabel/create?${qs.toString()}`, {
      state: {
        paymentReturnTo:
          returnTo && typeof returnTo === "string" && returnTo.startsWith("/")
            ? returnTo
            : `/whitelabel/details/${encodeURIComponent(whitelabelIdFromState)}?mode=active`,
        paymentPrefillData: {
          whitelabelId: whitelabelIdFromState,
          packageId: pkg.packageId,
          packageName: pkg.name?.trim() || "Plan",
          pointsCredited: pkg.pointsCredited,
          payAmount: pkg.payAmount,
          ...(pkg.discountAmount != null && Number.isFinite(pkg.discountAmount)
            ? { discountAmount: pkg.discountAmount }
            : {}),
          currency: pkg.currency ?? "INR",
          ...(pkg.packageDiscountPercent != null && Number.isFinite(pkg.packageDiscountPercent)
            ? { packageDiscountPercent: pkg.packageDiscountPercent }
            : {}),
          ...(pkg.paymentMethods?.length ? { paymentMethods: pkg.paymentMethods } : {}),
        },
      },
    });
  }, [navigate, packages, returnTo, selectedPackageId, whitelabelIdFromState]);

  return (
    <div className="text-white px-3 sm:px-4 pt-[5rem] pb-16 sm:pt-[5.5rem]">
      <div className="mx-auto max-w-4xl">
        <header className="mb-6 flex items-center gap-3">
          <button
            type="button"
            onClick={goBack}
            className="bg-inputbox-color flex h-8 w-8 shrink-0 items-center justify-center rounded"
            aria-label="Go back"
            title="Back"
          >
            <ArrowBackIcon className="text-white" style={{ width: 20, height: 20, color: "#FFFFFF" }} />
          </button>
          <h1 className="min-w-0 flex-1 text-xl font-semibold leading-tight text-zinc-100 sm:text-2xl">Packages</h1>
        </header>

        {!whitelabelIdFromState ? (
          <p className="text-sm text-amber-200/90">
            Open Add funds from a brand page so your account can load the right plans.
          </p>
        ) : null}

        {whitelabelIdFromState && loading ? (
          <p className="text-sm text-zinc-400">Loading plans…</p>
        ) : null}

        {whitelabelIdFromState && error && !loading ? (
          <div className="rounded-lg border border-amber-500/35 bg-amber-950/20 p-3">
            <p className="text-sm text-amber-100">{error}</p>
            <button type="button" className={BTN_SECONDARY_CLASS} onClick={() => setRetryNonce((n) => n + 1)}>
              Retry
            </button>
          </div>
        ) : null}

        {whitelabelIdFromState && !loading && !error && packages.length === 0 ? (
          <p className="text-sm text-zinc-400">No plans are available for this brand right now.</p>
        ) : null}

        {whitelabelIdFromState && !loading && !error && packages.length > 0 ? (
          <>
            <WhitelabelPackagePicker
              packages={packages}
              selectedPackageId={selectedPackageId}
              onSelectPackage={setSelectedPackageId}
              formatPrice={formatPackagePrice}
              formatPoints={formatPackagePoints}
            />
            <button type="button" className={BTN_PRIMARY_CLASS} disabled={!selectedPackageId} onClick={onContinue}>
              Continue
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default WhitelabelAddFundsPackages;
